/**
 * Basic configuration files.
 */

import merge from 'lodash-es/merge';
import set from 'lodash-es/set';
import toPairs from 'lodash-es/toPairs';

const env = process.env.CLASSPASS_ENV || process.env.NODE_ENV || 'local';
const envWithCluster = `${env}-${process.env.CP_CONFIG_PURPOSE || 'testing'}`;

const baseConfiguration = {
  envWithCluster: envWithCluster, // eslint-disable-line object-shorthand
  env: env, // eslint-disable-line object-shorthand
  app: {
    name: 'ClassPass Frontend',
    sanitizedName: 'classpass-frontend',
    api: 'https://api.classpass.com',
    ignoredErrorCodeExprs: [
      '^4(3\\d+|031|033|0331|034|038|04|401|405|608)$',
      '^5324$',
      '^429$', // User was rate limited
    ],
  },
  branchio: {
    key: 'key_test_kam80g4W5wmmGRKh6HaMUhfktxehSrXu',
  },
  cypress: {
    email: 'cypress+cfe@classpass.com',
    password: 'pk$321123',
  },
  geo: {
    enabled: false,
    datafilePath: './GeoIP2-City.mmdb',
    defaultCountry: 'USA',
  },
  google: {
    mapsKey: 'AIzaSyCyUB-J9qp5ZVGJ2Dzisd0L8moYcqf6k2U',
    geocoderKey: 'AIzaSyBb--OM8yjW0DmC7fUyfcBR5QzOalkSl84',
  },
  mobile: {
    ios: {
      appId: 912947244,
      providerId: 105659825,
      campaignToken: 'SmartApp%20Banner%20tracking',
    },
  },
  rollbar: {
    frontendToken: '3135b21fe156459f969457e3b8e246b6',
    backendToken: '0c36799ceb204d799905e6dc35d469cf',
  },
  sessions: {
    secrets: ['cl4a@!p', 'classpass$pk321'],
    cookieName: 'CP.SID',
  },
  datadog: {
    enabled: true,
    log: true,
    namespace: 'cp.cfe',
    appKey: '97bae6d2a963a50d9f4078544a77c17697ca3933',
    apiKey: '3b07a3229df2e7ace62144f66af1e9a0',
  },
  server: {
    showErrors: true,
    port: process.env.PORT || 3000,
    hstsEnabled: false,
  },
  greenhouse: {
    harvestApiKey: '9e0d8764f32d7b42847849e12312ba9f',
    boardsApiKey: 'fe22b191c69667446023c9244928d36e',
  },
  // DANGER WILL ROBINSON everything in this object is exposed to the client
  clientConfig: {
    env,
    unauthenticatedToken: '711016a6872c11e5a72022000bca01dd',
    clientId: '82ec597a-ad9d-11e4-bf55-22000b44012e',
    mixpanel: {
      key: '',
    },
    snowplow: {
      appId: 'classpass-frontend',
      host: 'development-use1-event-col.classpass.com',
      tracker: 'dev',
    },
    sift: {
      key: '39b5598140',
    },
    friendbuy: {
      default: {
        scriptUrl: '//djnf6e5yyirys.cloudfront.net/js/friendbuy.min.js',
        siteId: 'site-4eaf327d-www.classpass.com',
      },
      PHL: {
        scriptUrl: '//cdn.friendbuy.ph/js/friendbuy.min.js',
        siteId: 'site-ae6a390e-classpass.com',
      },
    },
    fullStory: {
      defaultSampleSize: 0.1,
      elevatedPaths: [],
      elevatedSampleSize: 0.33,
    },
    bitmovin: {
      analyticsKey: '7d361df5-24fd-4e42-98a7-47ab31293a5d',
      key: 'fced659c-2594-4cf2-aec5-2b04413a9e99',
    },
    optimizely: {
      projectId: 2341190220,
      experiments: {},
    },
    googleAnalytics: {
      cookieDomain: 'classpass.com',
    },
    googleTagManager: {
      container: '',
    },
    stripe: {
      pk: 'pk_09LW0Kb8lX1BjU41dybUeYHX38uVh',
    },
    mapbox: {
      apiKey: 'pk.eyJ1IjoiY2xhc3NwYXNzIiwiYSI6ImRxQWVNMWcifQ.HyK-gVf0sMT_1K9PNYe9fA',
      mapId: 'classpass.nd2n37ek',
      mapStyle: 'cjwmeg91n1cjw1cny2jf07p39',
    },
    chromecast: {
      applicationId: 'C85021B4',
    },
    pusher: {
      appKey: 'f57ef0e9b86672a482e4',
      cluster: 'us2',
    },
    retain: {
      appKey: 'd338acb979c8859beed663de184d1452',
    },
    segment: {
      key: 'A0zZtQVwWsGrKxZ64FWAeZFM0kiSUL7B',
    },
    platform: 'web',
  },
};

const overrides = {
  local: {
    app: {
      api: 'https://dev-api.classpass.com',
    },
    clientConfig: {
      bitmovin: {
        analyticsKey: 'a79492b5-1810-4bdd-8ffc-c80538584b56',
        key: '4e8fe190-51e8-41df-9097-c108285d0ab8',
      },
      chromecast: {
        applicationId: '7E860028',
      },
      mixpanel: {
        key: 'dd682b0aa9da8ef91a09c453e79b3ba1',
      },
      googleAnalytics: {
        key: 'UA-25463615-8',
      },
      googleTagManager: {
        container: 'GTM-PVCXGQ',
      },
      fb: {
        appId: '607537656035968',
      },
      optimizely: {
        projectId: '2341190220',
        experiments: {
          allowPhoneVerificationSkip: { id: '16708470151', variationId: '16744990358' },
        },
      },
      friendbuy: {
        default: {
          scriptUrl: '//djnf6e5yyirys.cloudfront.net/js/friendbuy.min.js',
          siteId: 'site-d713e579-develop.classpass.com',
        },
        PHL: {
          scriptUrl: '//cdn.friendbuy.ph/js/friendbuy.min.js',
          siteId: 'site-d47b9e9b-develop.classpass.com',
        },
      },
      pusher: {
        appKey: '121fdd0c72387f056217',
        cluster: 'us2',
      },
      segment: {
        key: 'A0zZtQVwWsGrKxZ64FWAeZFM0kiSUL7B',
      },
    },
  },
  development: {
    datadog: {
      enabled: true,
    },
    app: {
      api: 'https://dev-api.classpass.com',
    },
    geo: {
      enabled: true,
    },
    server: {
      showErrors: false,
    },
    clientConfig: {
      bitmovin: {
        analyticsKey: 'a79492b5-1810-4bdd-8ffc-c80538584b56',
        key: '4e8fe190-51e8-41df-9097-c108285d0ab8',
      },
      chromecast: {
        applicationId: 'A376C48D',
      },
      mixpanel: {
        key: 'dd682b0aa9da8ef91a09c453e79b3ba1',
      },
      googleAnalytics: {
        key: 'UA-25463615-8',
      },
      googleTagManager: {
        container: 'GTM-PVCXGQ',
      },
      fb: {
        appId: '607537422702658',
      },
      optimizely: {
        projectId: '2341190220',
        experiments: {
          paymentListCard: { id: '16613410428', variationId: '16621230424' },
          searchOnHome: { id: '16274970449', variationId: '16250480200' },
          allowPhoneVerificationSkip: { id: '16708470151', variationId: '16744990358' },
        },
      },
      friendbuy: {
        default: {
          scriptUrl: '//djnf6e5yyirys.cloudfront.net/js/friendbuy.min.js',
          siteId: 'site-d713e579-develop.classpass.com',
        },
        PHL: {
          scriptUrl: '//cdn.friendbuy.ph/js/friendbuy.min.js',
          siteId: 'site-d47b9e9b-develop.classpass.com',
        },
      },
      pusher: {
        appKey: '121fdd0c72387f056217',
        cluster: 'us2',
      },
      segment: {
        key: 'A0zZtQVwWsGrKxZ64FWAeZFM0kiSUL7B',
      },
    },
  },
  production: {
    geo: {
      enabled: true,
    },
    branchio: {
      key: 'key_live_phe4Yf3W3FgoUKMeZTbBRkaevveeJq6e',
    },
    datadog: {
      enabled: true,
    },
    server: {
      showErrors: false,
    },
    clientConfig: {
      mixpanel: {
        key: '13a079f3c862893a971ff2215ef81c76',
      },
      sift: {
        key: '0454019c60',
      },
      optimizely: {
        projectId: 2333360399,
        experiments: {
          paymentListCard: { id: '16600400181', variationId: '16625370148' },
          searchOnHome: { id: '16228780714', variationId: '16240890096' },
          // TODO
          // allowPhoneVerificationSkip: { id: '', variationId: '' },
        },
      },
      googleAnalytics: {
        key: 'UA-25463615-3',
      },
      googleTagManager: {
        container: 'GTM-NQ2Q9B',
      },
      fb: {
        appId: '205050386284699',
      },
      stripe: {
        pk: 'pk_09LWxpuEKGS6uapk3oo2iDyDbJuI5',
      },
      snowplow: {
        host: 'collector.classpass.com',
        tracker: 'prod',
      },
      chromecast: {
        applicationId: 'C85021B4',
      },
      pusher: {
        appKey: 'f57ef0e9b86672a482e4',
        cluster: 'us2',
      },
      segment: {
        key: '37Mk7zS9n03L38qTAG6qopa2c3ULxDBG',
      },
    },
  },
};

function classPassEnv([key]) {
  return key.indexOf('CP_') === 0 && key.indexOf('CP_CONFIG_') === -1;
}

function deserializeClassPassEnv(obj, [key, value]) {
  const path = key
    .replace(/^CP_(.*)/, '$1') // Remove CP_
    .replace('_', '.');

  try {
    const parsed = JSON.parse(value);
    return set(obj, path, parsed);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Unable to parse env var ${key} with value ${value} — is it valid JSON?\nError: ${error}`);
    return obj;
  }
}

const envOverrides = toPairs(process.env)
  .filter(classPassEnv)
  .reduce(deserializeClassPassEnv, {});

export const serverConfig = merge({}, baseConfiguration, overrides[env], envOverrides);
